/**
 * ☁️ Merge rules for the two meta stores that are NOT plain grow-only
 * (v3.61). Pure functions in utils so they can be tested directly — the
 * composables that own the state cannot run outside Nuxt.
 */

/**
 * 🌟 Stardust travels as two counters that only ever grow, and the
 * balance is derived: `balance = earned − spent`.
 *
 * Syncing the BALANCE and taking the max — the v1.17 rule — made spending
 * refundable: buy a 300-star creature on the phone, open the PC while it
 * still holds the pre-purchase number, and max() hands the 300 back with
 * the creature already in hand. Two grow-only halves cannot do that,
 * because the spend is itself a number that only climbs.
 */
export function mergeStardust(
  local: { earned: number; spent: number },
  remote: { earned?: number | null; spent?: number | null; balance?: number | null },
): { earned: number; spent: number } {
  const clean = (n: number | null | undefined) =>
    typeof n === 'number' && Number.isFinite(n) ? Math.max(0, Math.floor(n)) : null

  // 🏦 v4.92 (audit E2): the balance-only fallback is GONE. It credited a
  // bare `stardust` field as wholesale earnings — meant for v3.61-era
  // devices, long updated, and standing as a second door for writing any
  // number straight into the counters. A missing earned field now merges
  // as zero, exactly like every other grow-only counter.
  return {
    earned: Math.max(local.earned, clean(remote.earned) ?? 0),
    spent: Math.max(local.spent, clean(remote.spent) ?? 0),
  }
}

/**
 * ⭐ Favorites are LAST-WRITER-WINS, deliberately not a union.
 *
 * A favorite can be taken away, and a grow-only merge would make
 * unstarring impossible — drop a star on the phone and the PC's older
 * list puts it straight back on the next sync. So the whole list travels
 * with the moment it last changed and the newer edit wins outright.
 * Losing one star to a genuine race is a shrug; never being able to
 * unstar is a bug you feel every day.
 */
export function mergeFavorites(
  local: { list: string[]; at: number },
  remote: { list: unknown; at: number },
): { list: string[]; at: number } | null {
  if (!Array.isArray(remote.list)) return null
  if (!(remote.at > local.at)) return null
  return { list: remote.list.filter((v): v is string => typeof v === 'string'), at: remote.at }
}

/**
 * ☁️ v7.17 (follow-up review): the whole meta document, merged PURELY so
 * useCloudSync can do it inside a Firestore transaction. Numbers take
 * the max, lists the union, puzzle bests the min, favorites the newer
 * edit (mergeFavorites); the epoch is always this build's. `local` is
 * the snapshot this device is about to write, `remote` whatever the
 * transaction just read - the result is what gets written, and what the
 * device then adopts, so the two can never disagree.
 */
export interface MetaDoc {
  epoch: number
  ascensionUnlocked: number
  ascensionCleared: number[]
  collection: Record<string, string[]>
  collectionMastered: Record<string, string[]>
  puzzleBest: Record<string, number>
  favorites: string[]
  favoritesAt: number
  achievements: string[]
}

export function mergeMetaDocs<T extends MetaDoc>(local: T, remote: Record<string, unknown>): T {
  const strings = (v: unknown): string[] => Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
  const numbers = (v: unknown): number[] => Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number' && Number.isFinite(x)) : []
  const union = <V>(a: V[], b: V[]): V[] => [...new Set([...a, ...b])]
  const unionKinds = (mine: Record<string, string[]>, theirs: unknown): Record<string, string[]> => {
    const other = theirs && typeof theirs === 'object' ? theirs as Record<string, unknown> : {}
    const out: Record<string, string[]> = {}
    // This build's kinds only: an unknown kind is not something this
    // client can validate, and the rules would refuse the write anyway.
    for (const kind of Object.keys(mine)) out[kind] = union(mine[kind] ?? [], strings(other[kind]))
    return out
  }
  const remoteUnlocked = Number(remote.ascensionUnlocked)
  const bests: Record<string, number> = { ...local.puzzleBest }
  const theirBests = remote.puzzleBest && typeof remote.puzzleBest === 'object' ? remote.puzzleBest as Record<string, unknown> : {}
  for (const [level, damage] of Object.entries(theirBests)) {
    const dmg = Number(damage)
    if (!Number.isFinite(dmg)) continue
    const have = bests[level]
    bests[level] = have === undefined ? dmg : Math.min(have, dmg)
  }
  const favorites = mergeFavorites(
    { list: local.favorites, at: local.favoritesAt },
    { list: remote.favorites, at: Number(remote.favoritesAt ?? 0) },
  )
  return {
    ...local,
    ascensionUnlocked: Number.isFinite(remoteUnlocked) ? Math.max(local.ascensionUnlocked, remoteUnlocked) : local.ascensionUnlocked,
    ascensionCleared: union(local.ascensionCleared, numbers(remote.ascensionCleared)),
    collection: unionKinds(local.collection, remote.collection),
    collectionMastered: unionKinds(local.collectionMastered, remote.collectionMastered),
    puzzleBest: bests,
    favorites: favorites ? favorites.list : local.favorites,
    favoritesAt: favorites ? favorites.at : local.favoritesAt,
    achievements: union(local.achievements, strings(remote.achievements)),
  }
}
